// Club names (as FPL writes them) keyed by Opta team code, as used in the prediction data.
// Unknown codes fall back to the short name that ships with the data.
export const teamFullNames = {
    1: "Man Utd",
    2: "Leeds",
    3: "Arsenal",
    4: "Newcastle",
    6: "Spurs",
    7: "Aston Villa",
    8: "Chelsea",
    9: "Coventry",
    11: "Everton",
    13: "Leicester",
    14: "Liverpool",
    17: "Nott'm Forest",
    20: "Southampton",
    21: "West Ham",
    31: "Crystal Palace",
    36: "Brighton",
    39: "Wolves",
    40: "Ipswich",
    43: "Man City",
    54: "Fulham",
    56: "Sunderland",
    88: "Hull",
    90: "Burnley",
    91: "Bournemouth",
    94: "Brentford",
};

export const teamBadgeUrl = (teamCode) =>
    `https://omo.akamai.opta.net/image.php?secure=true&h=omo.akamai.opta.net&sport=football&entity=team&description=badges&dimensions=150&id=${teamCode}`;

// Heat-map fill from STYLE.md: brand blue for good, soft red for bad, alpha carries the size.
export const heatStyle = (alpha, positive = true) => {
    const a = Math.max(0, Math.min(1, alpha));
    return {
        background: positive ? `rgba(23, 89, 128, ${a})` : `rgba(200, 100, 100, ${a})`,
        color: a > 0.65 ? "white" : "var(--color-text-strong)",
    };
};

export const mean = (values) => values.reduce((sum, v) => sum + v, 0) / values.length;

// Strongest first (by xG difference, then xG for). Shared by the team strength and fixtures
// tables, so their rows line up.
export const sortTeamsByStrength = (teams) =>
    [...(teams || [])].sort((a, b) => (b.xg_diff - a.xg_diff) || (b.xg_for - a.xg_for));

// One row per team, strongest first, with its strength rank and its fixtures per gameweek:
// byGameweek[i] lists the team's fixtures in gameweeks[i] (none: blank, several: double).
// Each fixture: {id, opponent, venue, goals, cs} from the team's point of view.
export const buildTeamRows = (teams, fixtures) => {
    const gameweeks = Object.keys(fixtures || {}).map(Number).sort((a, b) => a - b);
    const byTeam = {};
    const add = (teamCode, gw, fixture) => {
        byTeam[teamCode] = byTeam[teamCode] || {};
        (byTeam[teamCode][gw] = byTeam[teamCode][gw] || []).push(fixture);
    };
    gameweeks.forEach(gw => {
        [...(fixtures[gw] || [])]
            .sort((a, b) => new Date(a.kickoff_time) - new Date(b.kickoff_time))
            .forEach(f => {
                add(f.home_team_code, gw, { id: f.fixture_id, opponent: f.away_team, venue: "H", goals: f.home_xg, cs: f.home_cs });
                add(f.away_team_code, gw, { id: f.fixture_id, opponent: f.home_team, venue: "A", goals: f.away_xg, cs: f.away_cs });
            });
    });
    const rows = sortTeamsByStrength(teams).map((t, idx) => ({
        ...t,
        rank: idx + 1,
        name: teamFullNames[t.team_code] || t.team_name,
        byGameweek: gameweeks.map(gw => byTeam[t.team_code]?.[gw] || []),
    }));
    return { gameweeks, rows };
};

// A team's value in one gameweek (summed over a double, 0 for a blank) and over all of them:
// expected goals, or clean sheet chances (summed: the expected number of clean sheets).
export const gameweekValue = (row, idx, metric) =>
    row.byGameweek[idx].reduce((sum, f) => sum + f[metric], 0);

export const totalValue = (row, metric) =>
    row.byGameweek.reduce((sum, _, idx) => sum + gameweekValue(row, idx, metric), 0);

// Sortable columns of the team strength and fixtures tables. Both tables show the teams in the
// same order (so their rows line up), sorted by one of these. A column first sorts best first.
const compareText = (a, b) => a.localeCompare(b);
export const TEAM_SORTS = {
    rank: { value: row => row.rank, bestFirst: "asc" },
    team: { value: row => row.name, bestFirst: "asc", compare: compareText },
    att: { value: row => row.xg_for, bestFirst: "desc" },
    def: { value: row => row.xg_against, bestFirst: "asc" },
    total: { value: (row, metric) => totalValue(row, metric), bestFirst: "desc" },
};
export const gameweekSortKey = (idx) => `gw${idx}`;

export const sortTeamRows = (rows, sort, metric) => {
    const gwMatch = /^gw(\d+)$/.exec(sort.key);
    const column = gwMatch
        ? { value: (row, m) => gameweekValue(row, Number(gwMatch[1]), m), bestFirst: "desc" }
        : TEAM_SORTS[sort.key];
    const compare = column.compare || ((a, b) => a - b);
    const direction = sort.direction === "asc" ? 1 : -1;
    // Ties keep the strength order
    return [...rows].sort((a, b) =>
        direction * compare(column.value(a, metric), column.value(b, metric)) || a.rank - b.rank);
};

export const defaultSortDirection = (key) =>
    /^gw\d+$/.test(key) ? "desc" : TEAM_SORTS[key].bestFirst;

// Heat style for signed deviations from the average (positive = better). Each side is scaled to
// its own extreme, so the best value gets the deepest blue and the worst the deepest red, even
// when the values are spread unevenly around the average (e.g. clean sheet chances: a few very
// high, none far below).
export const divergingHeatScale = (deviations) => {
    const maxBetter = Math.max(0, ...deviations) || 1;
    const maxWorse = Math.max(0, ...deviations.map(d => -d)) || 1;
    return (deviation) => deviation >= 0
        ? heatStyle((deviation / maxBetter) * 0.9, true)
        : heatStyle((-deviation / maxWorse) * 0.9, false);
};

// The rows in a given order of team codes (e.g. kept from before a view switch); teams not in it,
// or no order at all, go by strength
export const orderTeamRows = (rows, teamCodes) => {
    if (!teamCodes) return rows;
    const position = new Map(teamCodes.map((code, idx) => [code, idx]));
    const at = row => position.get(row.team_code) ?? Infinity;
    return [...rows].sort((a, b) => (at(a) - at(b)) || a.rank - b.rank);
};
